import React, { useState, useEffect, useMemo, useRef } from 'react';
// Schedule view pulls SCHEDULE sections, not SOV sections (those are
// fetched separately by SOVTab). Different concepts, different tables.
import { getTasks, getScheduleSections, getDependencyAlerts, getProject } from '../api';
import TasksTab from './TasksTab';
import GanttTab from './GanttTab';
import TaskEditModal from './TaskEditModal';
import PrintScheduleModal from './PrintScheduleModal';
import PrintScheduleView from './PrintScheduleView';
import { SCHEDULE_SORT_OPTIONS, classifyTaskStatus } from './scheduleSort';

const SCHEDULE_SPLIT_KEY = 'e3.scheduleSplitPct';

// Parent for the combined Schedule tab. Owns the shared task list,
// sections, collapse state and dep alerts so Tasks and Gantt always
// render the same rows in the same order.
const ScheduleView = ({ projectId, refreshSummary, onTaskOpen, refreshKey }) => {
  const [tasks, setTasks] = useState([]);
  const [savedSections, setSavedSections] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [depAlerts, setDepAlerts] = useState({ alerts: [], flagged_task_ids: [], summary: {} });
  const [loading, setLoading] = useState(true);
  // Schedule-wide search query lives here so both the Tasks panel AND
  // the Gantt panel filter in lockstep — otherwise the rows would
  // misalign between the two scrollable sides when the user is
  // hunting for a specific task.
  const [scheduleQuery, setScheduleQuery] = useState('');
  // Filter state — three independent dimensions, AND-combined.
  // Empty Set on any dimension means "no constraint" for that dimension.
  //   scheduleFilter.sections — multi-select of section names visible
  //     in this project. Section header for an excluded section is
  //     dropped from visibleRows entirely (no empty headers).
  //   scheduleFilter.statuses — subset of:
  //       'Not Started' | 'In Progress' | 'Complete'
  //     Classified from task.status (falls back to deriving from
  //     percent_complete: 0 = Not Started, 100 = Complete, else In Progress).
  //   scheduleFilter.types — subset of the six task_type values
  //     (Labor / Material / Procurement / Engineering / Inspection /
  //     Milestone).
  const [scheduleFilter, setScheduleFilter] = useState({
    sections: new Set(),
    statuses: new Set(),
    types: new Set(),
  });
  // Sort key referencing SCHEDULE_SORT_OPTIONS above. Applied
  // within-section so cross-section AIA grouping is preserved.
  const [scheduleSort, setScheduleSort] = useState('default');
  // Local edit modal state: when a Gantt bar is double-clicked, GanttTab calls
  // onTaskOpen which sets this id, opening TaskEditModal with the shared tasks.
  const [editTaskId, setEditTaskId] = useState(null);

  // Print Schedule state — opened from TasksTab's toolbar.
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printConfig, setPrintConfig] = useState(null); // { from, to, label, rangeType }
  const [projectName, setProjectName] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  useEffect(() => {
    if (!projectId) return;
    getProject(projectId).then(p => {
      setProjectName(p?.name || p?.project_name || '');
      setProjectNumber(p?.project_number || '');
    }).catch(() => {});
  }, [projectId]);

  const refreshTasks = async () => {
    try {
      const data = await getTasks(projectId);
      setTasks(data);
    } catch (e) { console.error('Error fetching tasks (shared):', e); }
  };
  const refreshSections = async () => {
    try {
      const data = await getScheduleSections(projectId);
      setSavedSections(data);
    } catch (e) { console.error('Error fetching sections (shared):', e); }
  };
  const refreshAlerts = async () => {
    try {
      const data = await getDependencyAlerts(projectId);
      setDepAlerts(data);
    } catch (e) { console.error('Error fetching dep alerts (shared):', e); }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([refreshTasks(), refreshSections(), refreshAlerts()]).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [projectId, refreshKey]);

  // Group by section using the same fallback name + same within-section sort
  // as TasksTab. This is the single source of truth.
  const groupedTasks = useMemo(() => {
    const groups = {};
    tasks.forEach(task => {
      const section = task.building_area_phase || 'Unsectioned';
      if (!groups[section]) groups[section] = [];
      groups[section].push(task);
    });
    Object.keys(groups).forEach(section => {
      groups[section].sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));
    });
    return groups;
  }, [tasks]);

  // Add gantt-style aliases (code/name/end_date/...) on each task so GanttTab
  // can read either field name without further changes inside that component.
  const aliasGantt = (t) => ({
    ...t,
    code: t.task_code,
    name: t.task_name,
    end_date: t.finish_date,
    duration: t.duration_days,
    progress: t.percent_complete,
    parent_id: t.parent_task_id,
    is_milestone: t.milestone_flag,
    dependencies: t.predecessors || [],
  });

  const sections = useMemo(() => {
    const savedNames = savedSections.map(s => s.section_name);
    const savedSet = new Set(savedNames);
    const allSectionNames = new Set([...savedNames, ...Object.keys(groupedTasks)]);
    const unsavedNames = Array.from(allSectionNames).filter(n => !savedSet.has(n)).sort();
    return [...savedNames.filter(n => allSectionNames.has(n)), ...unsavedNames];
  }, [savedSections, groupedTasks]);

  // Display-positional task_code override. The server already returns
  // task_code as TSK-NNNN based on global sort_order rank, but the
  // visible list groups by section -- so a Closeout section showing up
  // first alphabetically would still display TSK-0003 as its first row
  // (because tasks #1 and #2 belong to other sections that sort lower
  // in sort_order). Users see "TSK-0003 at the top" and read that as
  // wrong.
  // Rewrite task_code AT THE DISPLAY LAYER: iterate sections in the
  // order they'll appear on screen, then tasks within each section,
  // and stamp TSK-0001, TSK-0002, ... in that flat sequence. The new
  // codes propagate everywhere because predecessor / SOV-link /
  // dependent-task lookups all find tasks by id and read whatever
  // task_code came back. Both this tasks array AND groupedTasks need
  // the same codes so neither view drifts.
  const displayCodeMap = useMemo(() => {
    const map = new Map();
    let idx = 0;
    sections.forEach(section => {
      (groupedTasks[section] || []).forEach(t => {
        idx += 1;
        map.set(t.id, `TSK-${String(idx).padStart(4, '0')}`);
      });
    });
    return map;
  }, [sections, groupedTasks]);

  const tasksWithDisplayCodes = useMemo(
    () => tasks.map(t => ({ ...t, task_code: displayCodeMap.get(t.id) || t.task_code })),
    [tasks, displayCodeMap]
  );
  const groupedTasksWithDisplayCodes = useMemo(() => {
    const out = {};
    Object.entries(groupedTasks).forEach(([section, list]) => {
      out[section] = list.map(
        t => ({ ...t, task_code: displayCodeMap.get(t.id) || t.task_code })
      );
    });
    return out;
  }, [groupedTasks, displayCodeMap]);

  const ganttTasks = useMemo(
    () => tasksWithDisplayCodes.map(aliasGantt),
    [tasksWithDisplayCodes]
  );
  const groupedGanttTasks = useMemo(() => {
    const out = {};
    Object.entries(groupedTasksWithDisplayCodes).forEach(([section, list]) => {
      out[section] = list.map(aliasGantt);
    });
    return out;
  }, [groupedTasksWithDisplayCodes]);

  // Pre-compute the filtered + sorted groupedGanttTasks map. Both
  // panels (Tasks list AND Gantt) read from this, so the rows stay
  // in lockstep when the user toggles filters or sort.
  //
  // Filter layers (AND-combined):
  //   1. Search box (scheduleQuery) — code / name / building_area_phase
  //   2. Section filter — section name must be in the set (empty = all)
  //   3. Status filter — task's classified status must be in the set
  //   4. Type filter — task_type must be in the set
  const filteredGroupedGanttTasks = useMemo(() => {
    const q = scheduleQuery.trim().toLowerCase();
    const sectionSet = scheduleFilter.sections;
    const statusSet  = scheduleFilter.statuses;
    const typeSet    = scheduleFilter.types;
    const sortOpt = SCHEDULE_SORT_OPTIONS.find((o) => o.key === scheduleSort)
                    || SCHEDULE_SORT_OPTIONS[0];
    const defOpt = SCHEDULE_SORT_OPTIONS[0];

    const matches = (t, section) => {
      // 1. Search
      if (q) {
        const inSearch =
          ((t.task_code || t.code || '').toLowerCase().includes(q)) ||
          ((t.task_name || t.name || '').toLowerCase().includes(q)) ||
          ((t.building_area_phase || '').toLowerCase().includes(q));
        if (!inSearch) return false;
      }
      // 2. Section filter (section is the group key, not the task's
      //    building_area_phase, because tasks group by their section
      //    name and we want the FILTER to act on the same grouping)
      if (sectionSet.size > 0 && !sectionSet.has(section)) return false;
      // 3. Status filter
      if (statusSet.size > 0 && !statusSet.has(classifyTaskStatus(t))) return false;
      // 4. Type filter
      if (typeSet.size > 0 && !typeSet.has(t.task_type || '')) return false;
      return true;
    };

    const out = {};
    Object.entries(groupedGanttTasks).forEach(([section, list]) => {
      const filtered = list.filter((t) => matches(t, section));
      // Sort within section. Final tiebreaker = default code sort so
      // equal-value rows still group sensibly when not using default.
      const sorted = filtered.slice().sort((a, b) => {
        const primary = sortOpt.cmp(a, b);
        if (primary !== 0) return primary;
        return sortOpt === defOpt ? 0 : defOpt.cmp(a, b);
      });
      out[section] = sorted;
    });
    return out;
  }, [groupedGanttTasks, scheduleQuery, scheduleFilter, scheduleSort]);

  // Sister structure for the non-gantt-aliased rendering path used by
  // TasksTab. Same filtering/sort, same task ids, same display codes —
  // just without the .code/.name/.end_date aliases.
  const filteredGroupedTasks = useMemo(() => {
    const out = {};
    Object.entries(filteredGroupedGanttTasks).forEach(([section, list]) => {
      // Reverse the gantt-alias by reading from the existing
      // groupedTasksWithDisplayCodes map (which already has both raw
      // and display codes). We just keep the task objects in the same
      // order this filtered list dictates.
      const byId = new Map(
        (groupedTasksWithDisplayCodes[section] || []).map((t) => [t.id, t]),
      );
      out[section] = list.map((aliased) => byId.get(aliased.id)).filter(Boolean);
    });
    return out;
  }, [filteredGroupedGanttTasks, groupedTasksWithDisplayCodes]);

  // Filtered + sorted sections list.
  //
  // Two things happen here:
  //   1. Drop sections that have zero matching tasks WHEN any filter /
  //      search is active. With no filters, the full sections list is
  //      preserved so empty sections (typical for new projects) still
  //      show their headers + drop targets.
  //   2. When a non-default sort is active, REORDER the sections
  //      themselves so the globally-extreme task (e.g. earliest start
  //      date) ends up at the very top of the visible list — not just
  //      at the top of its own section. Each section is keyed by its
  //      first task (already sorted within section by the user's
  //      chosen cmp), so comparing first-of-A vs first-of-B with the
  //      same cmp ranks sections by representative value. Sections
  //      with no matching tasks sort to the end.
  const filteredSections = useMemo(() => {
    const hasAnyFilter =
      scheduleQuery.trim() !== '' ||
      scheduleFilter.sections.size > 0 ||
      scheduleFilter.statuses.size > 0 ||
      scheduleFilter.types.size > 0;
    let result = hasAnyFilter
      ? sections.filter((s) => (filteredGroupedGanttTasks[s] || []).length > 0)
      : sections;

    if (scheduleSort !== 'default') {
      const sortOpt = SCHEDULE_SORT_OPTIONS.find((o) => o.key === scheduleSort);
      if (sortOpt) {
        result = [...result].sort((a, b) => {
          const taskA = (filteredGroupedGanttTasks[a] || [])[0];
          const taskB = (filteredGroupedGanttTasks[b] || [])[0];
          if (!taskA && !taskB) return 0;
          if (!taskA) return 1;   // empty section to the end
          if (!taskB) return -1;
          return sortOpt.cmp(taskA, taskB);
        });
      }
    }
    return result;
  }, [sections, filteredGroupedGanttTasks, scheduleQuery, scheduleFilter, scheduleSort]);

  // Each task row carries the gantt-aliased task object so both panels can
  // read row.task directly. Aliased tasks include both the original field names
  // (task_code/task_name/finish_date) and the gantt aliases (code/name/end_date).
  const visibleRows = useMemo(() => {
    const rows = [];
    filteredSections.forEach((section) => {
      const sectionTasks = filteredGroupedGanttTasks[section] || [];
      rows.push({ type: 'section', section });
      if (!collapsedSections[section]) {
        sectionTasks.forEach((task) => {
          rows.push({ type: 'task', section, task });
        });
      }
    });
    return rows;
  }, [filteredSections, filteredGroupedGanttTasks, collapsedSections]);

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Project-wide earliest start / latest finish across all tasks (used to seed
  // the print range picker with the project's full duration).
  const projectDates = useMemo(() => {
    let minS = null;
    let maxE = null;
    tasks.forEach(t => {
      if (t.start_date && (!minS || t.start_date < minS)) minS = t.start_date;
      const end = t.finish_date || t.start_date;
      if (end && (!maxE || end > maxE)) maxE = end;
    });
    return { start: minS, end: maxE };
  }, [tasks]);

  const sharedSchedule = {
    // Surface the display-coded variants so TasksTab/GanttTab/etc. see
    // task_code values that match the visible position. The raw `tasks`
    // (with server-rank codes) is no longer exposed -- consumers
    // shouldn't accidentally read stale codes.
    tasks: tasksWithDisplayCodes,
    setTasks,
    ganttTasks,
    savedSections,
    setSavedSections,
    // Both the raw and filtered groupings flow through. TasksTab uses
    // the filtered ones for its sections.map loop so the search /
    // filter / sort apply there too; the raw ones stay available for
    // drag-and-drop section reordering and any other code that needs
    // the canonical unfiltered list.
    groupedTasks: groupedTasksWithDisplayCodes,
    groupedGanttTasks,
    filteredGroupedTasks,
    filteredGroupedGanttTasks,
    sections,
    filteredSections,
    visibleRows,
    collapsedSections,
    setCollapsedSections,
    toggleSection,
    depAlerts,
    refreshTasks,
    refreshSections,
    refreshAlerts,
    loading,
    openPrintModal: () => setPrintModalOpen(true),
    scheduleQuery,
    setScheduleQuery,
    scheduleFilter,
    setScheduleFilter,
    scheduleSort,
    setScheduleSort,
  };

  // Resizable split (lifted from the old inline ScheduleSplitView)
  const [leftPct, setLeftPct] = useState(() => {
    const stored = parseFloat(localStorage.getItem(SCHEDULE_SPLIT_KEY));
    return Number.isFinite(stored) && stored >= 15 && stored <= 85 ? stored : 40;
  });
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.max(15, Math.min(85, pct)));
    };
    const onUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem(SCHEDULE_SPLIT_KEY, String(leftPct));
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [leftPct]);

  const startDrag = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    // Sits inside .app-main's 24px gutter so the split-panel aligns
    // with the chrome above. Tasks + Gantt internal overflow handlers
    // take care of any horizontal scroll their own grids need.
    <div ref={containerRef} style={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
      {/* overflowX: 'auto' (was 'clip') so the task table gets a horizontal
          scrollbar when columns exceed the splitter width — otherwise the
          right-most columns are unreachable when the user drags the
          splitter narrow. */}
      <div style={{ width: `${leftPct}%`, minWidth: 0, overflowX: 'auto' }}>
        <TasksTab
          projectId={projectId}
          refreshSummary={refreshSummary}
          sharedSchedule={sharedSchedule}
          // Without this, double-clicking a row in the task list panel
          // falls back to TasksTab's local inline modal -- which has a
          // preview-only SOV Links section (no add UI) and a slimmer
          // overall layout. Passing onTaskOpen routes the click to the
          // SAME TaskEditModal that GanttTab uses, so both panels of
          // the Schedule view open the identical properties dialog with
          // the full <TaskSOVLinks> component.
          onTaskOpen={(id) => setEditTaskId(id)}
        />
      </div>
      <SplitHandle onMouseDown={startDrag} />
      <div style={{ width: `${100 - leftPct}%`, minWidth: 0 }}>
        <GanttTab
          projectId={projectId}
          refreshSummary={refreshSummary}
          onTaskOpen={(id) => setEditTaskId(id)}
          refreshKey={refreshKey}
          inSplitView
          sharedSchedule={sharedSchedule}
        />
      </div>

      {editTaskId && (
        <TaskEditModal
          taskId={editTaskId}
          projectId={projectId}
          availableTasks={tasks}
          onClose={() => setEditTaskId(null)}
          onSaved={() => { refreshTasks(); refreshAlerts(); refreshSummary && refreshSummary(); }}
          onDeleted={() => { setEditTaskId(null); refreshTasks(); refreshAlerts(); refreshSummary && refreshSummary(); }}
          // Fires every time the user adds/removes an SOV link inside
          // the embedded TaskSOVLinks section. refreshTasks re-fetches
          // the task list AND repopulates the taskAllocations map in
          // TasksTab, so the SOV-link badges on each row stay in sync
          // even if the user closes the modal via X (no save).
          onAllocationsChanged={() => { refreshTasks(); refreshSummary && refreshSummary(); }}
        />
      )}

      <PrintScheduleModal
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        onConfirm={(cfg) => { setPrintConfig(cfg); setPrintModalOpen(false); }}
        projectStart={projectDates.start}
        projectEnd={projectDates.end}
      />

      {printConfig && (
        <PrintScheduleView
          projectName={projectName}
          projectNumber={projectNumber}
          rangeLabel={printConfig.label}
          from={printConfig.from}
          to={printConfig.to}
          tasks={tasks}
          sections={sections}
          groupedTasks={groupedTasks}
          onClose={() => setPrintConfig(null)}
        />
      )}
    </div>
  );
};

export default ScheduleView;

/* -------------------- SplitHandle --------------------
 * Schedule handoff §6 "Splitter":
 *   - 7px wide hot zone, cursor: col-resize
 *   - On hover/drag, reveal a 3px × 32px pill at 30% mix of --accent
 *     and --border-default
 */
function SplitHandle({ onMouseDown }) {
  const [hover, setHover] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const active = hover || dragging;
  return (
    <div
      onMouseDown={(e) => { setDragging(true); onMouseDown(e); }}
      onMouseUp={() => setDragging(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="Drag to resize"
      style={{
        alignSelf: 'stretch',
        width: 7,
        flexShrink: 0,
        cursor: 'col-resize',
        background: 'transparent',
        position: 'relative',
        // No persistent borders — the hover pill is the only visual.
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 3,
          height: 32,
          borderRadius: 2,
          background: 'color-mix(in oklch, var(--accent) 30%, var(--border-default))',
          opacity: active ? 1 : 0,
          transition: 'opacity .12s',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
